import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Header navigation.
 *
 * Rewritten to drop Shopify's <overflow-list> wrapper. The old implementation
 * re-assigned `element.slot` on every reflow, which physically re-parents the
 * <a> elements in the shadow tree. Because a reflow was triggered by the very
 * hover that opens a submenu (it changes --submenu-height, which resizes the
 * header, which notifies the list's ResizeObserver), the link under the cursor
 * moved between mousedown and mouseup and the browser never produced a click.
 * That is why links needed several attempts to open.
 *
 * This version never moves a node. It only toggles attributes, so a pointer
 * that lands on a link stays on that link.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} nav
 * @property {HTMLElement} [list]
 * @property {HTMLElement[]} [items]
 * @property {HTMLAnchorElement[]} [triggers]
 * @property {HTMLElement[]} [panels]
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['nav'];

  /** @type {HTMLElement | null} */
  #openItem = null;

  /** @type {number | null} */
  #openTimer = null;

  /** @type {number | null} */
  #closeTimer = null;

  /**
   * Set once a submenu has been opened by tapping on a touch device, so the
   * second tap on the same trigger is allowed through as navigation.
   * @type {HTMLElement | null}
   */
  #tapArmed = null;

  connectedCallback() {
    super.connectedCallback();

    for (const item of this.#items) {
      if (!this.#panelOf(item)) continue;

      item.addEventListener('pointerenter', this.#onPointerEnter);
      item.addEventListener('pointerleave', this.#onPointerLeave);
    }

    for (const trigger of this.#triggers) {
      trigger.addEventListener('click', this.#onTriggerClick);
      trigger.addEventListener('focus', this.#onTriggerFocus);
    }

    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown);

    this.#syncBridge();

    onDocumentLoaded(() => {
      this.#preloadImages();
      // Re-measure once fonts and the announcement bar have settled.
      requestAnimationFrame(this.#syncBridge);
    });

    // The sticky header changes its own padding as it detaches, and the
    // announcement bar can be dismissed — both move the header's bottom edge.
    window.addEventListener('resize', this.#scheduleSyncBridge);
    document.addEventListener('scroll', this.#scheduleSyncBridge, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown);
    window.removeEventListener('resize', this.#scheduleSyncBridge);
    document.removeEventListener('scroll', this.#scheduleSyncBridge);
    this.#clearTimers();

    if (this.#bridgeRafId !== null) {
      cancelAnimationFrame(this.#bridgeRafId);
      this.#bridgeRafId = null;
    }
  }

  /* ── configuration ─────────────────────────────────────────────── */

  get #openDelay() {
    return Number(this.dataset.openDelay ?? 60);
  }

  get #closeDelay() {
    return Number(this.dataset.closeDelay ?? 180);
  }

  get #headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /** @returns {HTMLElement[]} */
  get #items() {
    return toArray(this.refs.items);
  }

  /** @returns {HTMLAnchorElement[]} */
  get #triggers() {
    return toArray(this.refs.triggers);
  }

  /* ── opening and closing ───────────────────────────────────────── */

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #panelOf(item) {
    const panel = item.querySelector(':scope > [ref="panels[]"]');
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLAnchorElement | null}
   */
  #triggerOf(item) {
    const trigger = item.querySelector(':scope > .menu-list__link');
    return trigger instanceof HTMLAnchorElement ? trigger : null;
  }

  #clearTimers() {
    if (this.#openTimer !== null) clearTimeout(this.#openTimer);
    if (this.#closeTimer !== null) clearTimeout(this.#closeTimer);
    this.#openTimer = null;
    this.#closeTimer = null;
  }

  /**
   * @param {HTMLElement} item
   */
  #open(item) {
    this.#clearTimers();

    if (this.#openItem === item) return;
    if (this.#openItem) this.#close(this.#openItem);

    const panel = this.#panelOf(item);
    const trigger = this.#triggerOf(item);
    if (!panel || !trigger) return;

    this.#openItem = item;
    trigger.setAttribute('aria-expanded', 'true');

    this.#syncBridge();
    this.#alignPanel(panel, item);

    // The mega-menu variant is clipped by --submenu-height, which the header
    // reads to size its backdrop. Simple dropdowns size themselves.
    if (!panel.hasAttribute('data-simple')) {
      this.#headerComponent?.style.setProperty('--submenu-height', `${panel.offsetHeight}px`);
      this.style.setProperty('--submenu-opacity', '1');
    }

    this.dispatchEvent(new MegaMenuHoverEvent());
  }

  /**
   * @param {HTMLElement} [item]
   */
  #close(item = this.#openItem ?? undefined) {
    if (!item) return;

    const trigger = this.#triggerOf(item);
    trigger?.setAttribute('aria-expanded', 'false');

    const panel = this.#panelOf(item);
    panel?.removeAttribute('data-align');

    if (this.#openItem === item) {
      this.#openItem = null;
      this.#headerComponent?.style.setProperty('--submenu-height', '0px');
      this.style.setProperty('--submenu-opacity', '0');
    }

    if (this.#tapArmed === item) this.#tapArmed = null;
  }

  #closeAll() {
    this.#clearTimers();
    if (this.#openItem) this.#close(this.#openItem);
    this.#tapArmed = null;
  }

  /**
   * Sets the panel's top padding to the exact distance between the bottom of
   * the trigger and the bottom edge of the header, so the panel hangs flush
   * off the header bar with no seam.
   *
   * The CSS fallback derives this from --header-padding, but that is only an
   * assumption about the theme's spacing: sticky state, the announcement bar
   * and the row layout all shift it. Measuring is exact and self-correcting.
   *
   * The padding is transparent, so it doubles as the hover bridge that keeps
   * the pointer inside the <li> on its way down to the panel.
   *
   * @param {HTMLElement} item
   * @param {HTMLElement} panel
   */
  /**
   * Sizes every panel's top padding to the exact distance between the bottom
   * of its trigger and the bottom edge of the header, so each panel hangs
   * flush off the bar with no seam.
   *
   * The padding is transparent, so it doubles as the hover bridge that keeps
   * the pointer inside the <li> on its way down into the panel.
   *
   * Written straight onto each panel as an inline style rather than as an
   * inherited custom property. The value has to survive the cascade no matter
   * what else in the theme touches padding on this element, and an inline
   * declaration is the one thing that cannot be lost to specificity, to a
   * shorthand reset, or to a var() that fails to resolve.
   *
   * Runs before the pointer arrives, not on open: CSS :hover reveals the panel
   * the instant the cursor lands, which is earlier than the JS hover-intent
   * delay, so measuring only in #open() would always be one step behind.
   */
  #syncBridge = () => {
    const header = this.#headerComponent;
    if (!header) return;

    const headerBottom = header.getBoundingClientRect().bottom;

    for (const item of this.#items) {
      const panel = this.#panelOf(item);
      if (!panel) continue;

      // The mega-menu variant is full-width and clipped by --submenu-height;
      // it does its own thing and must not be padded.
      if (!panel.hasAttribute('data-simple')) continue;

      const distance = headerBottom - item.getBoundingClientRect().bottom;

      // A negative or absurd reading means the header is mid-transition; keep
      // the last good value rather than committing a bad one.
      if (!Number.isFinite(distance) || distance < 0 || distance > 200) continue;

      // Floor rather than round: a fractional shortfall tucks the panel a
      // sub-pixel under the header, which is invisible. Rounding up would
      // leave a hairline of background showing through — the visible gap.
      panel.style.paddingBlockStart = `${Math.floor(distance)}px`;
    }
  };

  /** @type {number | null} */
  #bridgeRafId = null;

  /**
   * rAF-throttled #syncBridge for the scroll and resize listeners — measuring
   * on every raw scroll event would force a layout read per frame or worse.
   */
  #scheduleSyncBridge = () => {
    if (this.#bridgeRafId !== null) return;

    this.#bridgeRafId = requestAnimationFrame(() => {
      this.#bridgeRafId = null;
      this.#syncBridge();
    });
  };

  /**
   * Lines the panel up with the trigger's visible pill, then flips it to the
   * right edge if that would push it past the viewport.
   *
   * The panel is positioned against the <li>, but the pill the user actually
   * sees is .menu-list__link-title, which is centred inside a full-width
   * anchor. Whenever the anchor is wider than its label the two left edges do
   * not coincide, and the panel reads as offset from the button above it.
   * Measuring the pill removes that mismatch regardless of how the theme's
   * padding settings are configured.
   *
   * Reads layout but never moves the node.
   *
   * @param {HTMLElement} panel
   * @param {HTMLElement} item
   */
  #alignPanel(panel, item) {
    panel.removeAttribute('data-align');
    panel.style.insetInlineStart = '';

    const pill = item.querySelector('.menu-list__link-title');

    if (pill instanceof HTMLElement) {
      const offset = pill.getBoundingClientRect().left - item.getBoundingClientRect().left;

      if (Number.isFinite(offset) && offset >= 0 && offset < 200) {
        panel.style.insetInlineStart = `${Math.round(offset)}px`;
      }
    }

    const margin = 16;

    if (panel.getBoundingClientRect().right > window.innerWidth - margin) {
      // Anchor to the trailing edge instead; the inline offset would fight it.
      panel.style.insetInlineStart = '';
      panel.setAttribute('data-align', 'end');
    }
  }

  /* ── pointer ───────────────────────────────────────────────────── */

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    // Touch taps also emit pointerenter; those are handled by the click path
    // so a tap does not open and immediately navigate.
    if (event.pointerType === 'touch') return;

    const item = event.currentTarget;
    if (!(item instanceof HTMLElement)) return;

    // Both of these run synchronously, in the same event turn that CSS :hover
    // starts revealing the panel, so the very first painted frame is already
    // bridged and aligned. Doing it only in #open() — after the hover-intent
    // delay — meant the panel was always visible for a moment in the wrong
    // place first.
    this.#syncBridge();

    const enteredPanel = this.#panelOf(item);
    if (enteredPanel) this.#alignPanel(enteredPanel, item);

    this.#clearTimers();

    // Moving between two top-level items with a menu already open should feel
    // instant; only the first open waits for the intent delay.
    const delay = this.#openItem ? 0 : this.#openDelay;

    this.#openTimer = window.setTimeout(() => this.#open(item), delay);
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType === 'touch') return;

    const item = event.currentTarget;
    if (!(item instanceof HTMLElement)) return;

    if (this.#openTimer !== null) {
      clearTimeout(this.#openTimer);
      this.#openTimer = null;
    }

    if (this.#closeTimer !== null) clearTimeout(this.#closeTimer);

    this.#closeTimer = window.setTimeout(() => {
      // Do not close if the pointer has landed back inside the item (the
      // invisible bridge above the panel keeps it within bounds).
      if (item.matches(':hover')) return;
      this.#close(item);
    }, this.#closeDelay);
  };

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const item = trigger.closest('.menu-list__list-item');
    if (!(item instanceof HTMLElement)) return;
    if (!this.#panelOf(item)) return;

    const coarse = window.matchMedia('(hover: none)').matches;
    if (!coarse) return;

    // Touch: first tap reveals the submenu, second tap follows the link.
    if (this.#tapArmed === item) return;

    event.preventDefault();
    this.#open(item);
    this.#tapArmed = item;
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    if (!this.#openItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;
    this.#closeAll();
  };

  /* ── keyboard ──────────────────────────────────────────────────── */

  /** @param {FocusEvent} event */
  #onTriggerFocus = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    const item = trigger.closest('.menu-list__list-item');
    if (!(item instanceof HTMLElement)) return;

    if (this.#panelOf(item)) {
      this.#open(item);
    } else {
      this.#closeAll();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      if (!this.#openItem) return;
      const item = this.#openItem;
      this.#closeAll();
      this.#triggerOf(item)?.focus();
      event.stopPropagation();
      return;
    }

    if (event.key === 'ArrowDown' && this.#openItem) {
      const target = event.target;
      const isTrigger = target instanceof HTMLElement && target.matches('.menu-list__link');
      if (!isTrigger) return;

      const panel = this.#panelOf(this.#openItem);
      const first = panel?.querySelector('a[href]');
      if (first instanceof HTMLElement) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  /*
   * Deliberately no overflow → drawer measurement here.
   *
   * An earlier version measured the nav and flagged the header when it did not
   * fit. That flag hides .header-menu, which changes what there is to measure,
   * so the check kept contradicting itself — first oscillating (a permanent
   * flicker), then latching into drawer mode on a full-width desktop and hiding
   * the navigation and the currency/language selectors altogether.
   *
   * Whether to show the inline menu or the drawer is left to the theme's own
   * setHeaderMenuStyle(), exactly as it was before this component was rewritten.
   */

  #preloadImages = () => {
    for (const image of this.querySelectorAll('img[loading="lazy"]')) {
      image.removeAttribute('loading');
    }
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Normalises a `ref="name[]"` value, which may be a single node or a list.
 * @template T
 * @param {T | T[] | undefined | null} value
 * @returns {T[]}
 */
function toArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}
